import { useAuth } from '@agentnet/auth';
import { createStyles, Grid, makeStyles, Theme } from '@material-ui/core';
import { GetFileInfoProps } from 'api/file/file-api';
import { updateFincenReportingPartyAPI } from 'api/fincen-report-api';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import { useAutoSaveNavigation, useFincenAutoSave } from 'hooks/useAutoSave';
import { useNavigationLoading } from 'hooks/useNavigationLoading';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { Redirect, Route, Switch, useHistory, useParams } from 'react-router-dom';
import BottomAppBar from 'ui-kit/components/bottomAppBar/BottomAppBar';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import { useMenuContext } from '../ReportingLayout';
import ReportPartyInformationContent from './ReportPartyInformationContent';

const ReportPartyInformationContainer: FC = () => {
  const { getAccessToken } = useAuth();
  const { fileId } = useParams<{ fileId: string }>();
  const history = useHistory();
  const fileDataContext = useContext(FileDataContext) as FileDataContextInterface;
  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const { fincenReportStatus } = fincenReportCtx ?? {};

  const { menuCollapsed } = useMenuContext();

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      contentContainer: {
        paddingBottom: theme.spacing(8),
        display: 'flex',
        flexDirection: 'column',
      },
      mainContainer: {
        width: '100%',
        position: 'relative',
      },
      contentWrapper: {
        overflow: 'auto',
        paddingBottom: theme.spacing(8),
      },
    }),
  );

  const classes = useStyles();

  useEffect(() => {
    if (fileId && fileDataContext && !fileDataContext.fileData && fileDataContext.loadFileData) {
      const fileProps: GetFileInfoProps = {
        fileId,
      };
      fileDataContext.loadFileData(fileProps);
    }
  }, [fileId, fileDataContext]);

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [shouldNavigateOnComplete, setShouldNavigateOnComplete] = useState(false);
  const isNavigating = useNavigationLoading();

  const handleAutoSave = useCallback(async (): Promise<void> => {
    if (!fincenReportCtx?.fincenReport) return;
    if (fincenReportCtx.fincenReport.reportStatusCdId === 9302) return; // Disable auto-save once report generated

    const reportingPartyData = fincenReportCtx.fincenReport.reportingParty;
    if (!reportingPartyData) return;

    const token = await getAccessToken();
    await updateFincenReportingPartyAPI(
      String(fincenReportCtx.fincenReport.fincenReportId || 0),
      reportingPartyData,
      token,
    );
  }, [fincenReportCtx, getAccessToken]);

  useAutoSaveNavigation(
    'reporting-party',
    handleAutoSave,
    fileId,
    fincenReportCtx?.fincenReport?.reportStatusCdId !== 9302,
  );

  const { triggerSave, isSaving } = useFincenAutoSave({
    data: fincenReportCtx?.fincenReport?.reportingParty,
    updateFunction: fincenReportCtx?.updateReportingParty,
    apiSaveFunction: updateFincenReportingPartyAPI,
    reportId: fincenReportCtx?.fincenReport?.fincenReportId,
    hasContent: (data) => !!(data && (data.firstName || data.lastName || data.entityName)),
    enabled: fincenReportCtx?.fincenReport?.reportStatusCdId !== 9302,
    errorContext: 'reporting party information',
  });

  const handleSavePartyInfo = () => {
    setShouldNavigateOnComplete(false);
    setFormSubmitted(true);
  };
  const handleFormSubmissionComplete = (forceNavigate?: boolean) => {
    setFormSubmitted(false);
    if (forceNavigate || shouldNavigateOnComplete) {
      history.push(`/files/${fileId}/fincen-reporting/property-info`);
      setShouldNavigateOnComplete(false);
    }
  };

  const handleBackToFilingInformation = () => {
    history.push(`/files/${fileId}/fincen-reporting/filing-information`);
  };

  const handleNext = () => {
    if (fincenReportCtx?.fincenReport?.reportStatusCdId === 9302) {
      history.push(`/files/${fileId}/fincen-reporting/property-info`);
      return;
    }
    setShouldNavigateOnComplete(true);
    setFormSubmitted(true);
  };
  if (!fileId) {
    return <Redirect to="/files" />;
  }

  return (
    <div data-testid="fincen-reporting-party-information-container" className={classes.mainContainer}>
      <LoadingSpinner
        status={formSubmitted || isNavigating || fincenReportStatus === 'pending' ? 'pending' : 'success'}
        variant="linear"
      />
      <Grid container spacing={0}>
        <Grid item xs className={classes.contentWrapper}>
          <div className={classes.contentContainer}>
            <Switch>
              <Route
                exact
                path="/files/:fileId/fincen-reporting/report-party"
                render={() => (
                  <ReportPartyInformationContent
                    fileData={fileDataContext?.fileData}
                    menuCollapsed={menuCollapsed}
                    formSubmitted={formSubmitted}
                    onFormSubmissionComplete={handleFormSubmissionComplete}
                    isReadOnly={fincenReportCtx?.fincenReport?.reportStatusCdId === 9302}
                  />
                )}
              />
            </Switch>
          </div>
        </Grid>
      </Grid>
      <BottomAppBar
        cancelLabel="Back"
        onCancel={handleBackToFilingInformation}
        cancelDataQa="fincen-report-party-back-button"
        secondaryActionLabel="Save Report"
        onSecondaryClick={handleSavePartyInfo}
        secondaryActionDataQa="fincen-report-party-save-button"
        primaryActionLabel="Next"
        onPrimaryClick={handleNext}
        primaryActionDataQa="fincen-report-party-next-button"
        disableSecondary={fincenReportCtx?.fincenReport?.reportStatusCdId === 9302}
      />
    </div>
  );
};
export default ReportPartyInformationContainer;
